import React, { useState, useRef } from 'react';

interface Photo {
  id?: string;
  fileName: string;
  originalFileName: string;
  filePath: string;
  fileSize: number;
  mimeType: string;
  displayOrder?: number;
}

interface Post {
  id: string;
  title: string;
  content: string;
  isActive: boolean;
  photos: Photo[];
}

interface GirlsCornerEditorProps {
  post: Post | null;
  onClose: () => void;
  onSuccess: () => void;
}

export default function GirlsCornerEditor({ post, onClose, onSuccess }: GirlsCornerEditorProps) {
  const [title, setTitle] = useState(post?.title || '');
  const [content, setContent] = useState(post?.content || '');
  const [setActive, setSetActive] = useState(false);
  const [photos, setPhotos] = useState<Photo[]>(post?.photos || []);
  const [uploadingPhotos, setUploadingPhotos] = useState<File[]>([]);
  const [photosToDelete, setPhotosToDelete] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const newFiles = Array.from(files).filter(file => {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          alert(`${file.name} is not an image file`);
          return false;
        }
        // Validate file size (10MB)
        if (file.size > 10 * 1024 * 1024) {
          alert(`${file.name} exceeds 10MB limit`);
          return false;
        }
        return true;
      });
      
      setUploadingPhotos([...uploadingPhotos, ...newFiles]);
    }
  };

  const handleRemoveUploadingPhoto = (index: number) => {
    setUploadingPhotos(uploadingPhotos.filter((_, i) => i !== index));
  };

  const handleRemoveExistingPhoto = (photoId: string) => {
    setPhotosToDelete([...photosToDelete, photoId]);
    setPhotos(photos.filter(p => p.id !== photoId));
  };

  const uploadPhoto = async (file: File): Promise<Photo | null> => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/upload/girlscorner-photo', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        return {
          fileName: data.data.fileName,
          originalFileName: data.data.originalFileName,
          filePath: data.data.filePath,
          fileSize: data.data.fileSize,
          mimeType: data.data.mimeType
        };
      } else {
        throw new Error(data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Photo upload error:', error);
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors([]);

    // Validate
    if (!title.trim()) {
      setErrors(['Title is required']);
      setLoading(false);
      return;
    }
    if (!content.trim()) {
      setErrors(['Content is required']);
      setLoading(false);
      return;
    }

    try {
      // Upload new photos
      const uploadedPhotos: Photo[] = [];
      for (const file of uploadingPhotos) {
        const uploaded = await uploadPhoto(file);
        if (uploaded) {
          uploadedPhotos.push(uploaded);
        } else {
          setErrors([...errors, `Failed to upload ${file.name}`]);
        }
      }

      const token = localStorage.getItem('authToken');
      const url = post ? `/api/girlscorner/${post.id}` : '/api/girlscorner';
      const method = post ? 'PUT' : 'POST';

      const body: any = {
        title: title.trim(),
        content: content.trim(),
        photos: uploadedPhotos
      };

      if (!post) {
        // Only set active on new posts
        body.setActive = setActive;
      } else {
        // For updates, include photos to delete
        body.photosToDelete = photosToDelete;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      if (data.success) {
        onSuccess();
      } else {
        setErrors([data.message || 'Failed to save post']);
      }
    } catch (error) {
      console.error('Save error:', error);
      setErrors(['Failed to save post. Please try again.']);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-ggaa-green to-ggaa-dark text-white px-8 py-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">
              {post ? '✏️ Edit Post' : '✨ Create New Post'}
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-3xl leading-none"
              disabled={loading}
            >
              ×
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-8">
          {/* Errors */}
          {errors.length > 0 && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <ul className="list-disc list-inside text-red-700 text-sm space-y-1">
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Title */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
              placeholder="e.g., Girls' Corner - November 15, 2024"
              required
              disabled={loading}
            />
          </div>

          {/* Content */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Content *
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={12}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ggaa-green focus:border-transparent font-mono text-sm"
              placeholder="Enter the Girls' Corner content here. Line breaks will be preserved."
              required
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-2">
              💡 Tip: Use line breaks to format your content. Each line will appear on its own line.
            </p>
          </div>

          {/* Photos Section */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Photos (Optional)
            </label>
            
            {/* Existing Photos */}
            {photos.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-gray-600 mb-2">Existing Photos:</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {photos.map((photo) => (
                    <div key={photo.id} className="relative group">
                      <img
                        src={`/uploads/girlscorner/${photo.fileName}`}
                        alt={photo.originalFileName}
                        className="w-full h-24 object-cover rounded-lg border-2 border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveExistingPhoto(photo.id!)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity"
                        disabled={loading}
                      >
                        ×
                      </button>
                      <p className="text-xs text-gray-600 mt-1 truncate">{photo.originalFileName}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* New Photos to Upload */}
            {uploadingPhotos.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-gray-600 mb-2">New Photos to Upload:</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {uploadingPhotos.map((file, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        className="w-full h-24 object-cover rounded-lg border-2 border-green-300"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveUploadingPhoto(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity"
                        disabled={loading}
                      >
                        ×
                      </button>
                      <p className="text-xs text-gray-600 mt-1 truncate">{file.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upload Button */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-ggaa-green hover:text-ggaa-green transition-colors flex items-center justify-center gap-2"
              disabled={loading}
            >
              <span className="text-2xl">📸</span>
              <span>Click to add photos</span>
            </button>
            <p className="text-xs text-gray-500 mt-2">
              You can select multiple images. Max 10MB per image. Supported formats: JPG, PNG, GIF, WebP
            </p>
          </div>

          {/* Set as Active (only for new posts) */}
          {!post && (
            <div className="mb-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={setActive}
                  onChange={(e) => setSetActive(e.target.checked)}
                  className="w-5 h-5 text-ggaa-green rounded focus:ring-ggaa-green"
                  disabled={loading}
                />
                <span className="text-sm font-medium text-gray-700">
                  Set as active post immediately (will deactivate other posts)
                </span>
              </label>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-medium"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-ggaa-green text-white rounded-lg hover:bg-ggaa-dark transition-colors font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Saving...' : post ? 'Update Post' : 'Create Post'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

